/**
 * Sticky buy — barra de compra fixa na PDP.
 * Reconstrução para Shopify da referência Champion/Sportbras (Nuvemshop).
 *
 * - Modos por dispositivo (breakpoint 1199.98px): 'button' (mostra quando o botão
 *   real de compra sai da tela), 'always', 'scroll' (após o 1º scroll) e 'off'.
 * - Espelha preço/variação/estado do CTA a partir dos elementos reais da PDP,
 *   reagindo às re-renderizações do <product-information> na troca de variação.
 * - CTA (comportamento Champion): com variação → rola até os seletores; sem → dispara
 *   o add-to-cart real (mantém a validação/AJAX do <product-form>).
 */
(function () {
  'use strict';

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.modeDesktop = this.dataset.modeDesktop || 'off';
      this.modeMobile = this.dataset.modeMobile || 'off';
      this.hasVariants = this.dataset.hasVariants === 'true';
      this.customLabel = (this.dataset.customLabel || '').trim();

      // Nenhum modo ativo → não faz nada.
      if (this.modeDesktop === 'off' && this.modeMobile === 'off') return;

      this.mql = window.matchMedia('(max-width: 1199.98px)');

      this.productInfo =
        document.querySelector('product-information[data-section-id="' + this.sectionId + '"]') ||
        document.querySelector('product-information');

      this.realBtn = document.getElementById('ProductSubmitButton-' + this.sectionId);
      this.cta = this.querySelector('.js-custom-sticky-buy-cta');
      this.priceEl = this.querySelector('.js-custom-sticky-buy-price');
      this.compareEl = this.querySelector('.js-custom-sticky-buy-compare');
      this.variantEl = this.querySelector('.js-custom-sticky-buy-variant');

      // Elementos flutuantes que a barra não pode cobrir (empurrados pra cima quando visível).
      // Resolvidos de forma lazy porque o botão de WhatsApp é injetado por app em runtime.
      this._floatCache = {};

      this._onScroll = this._onScroll.bind(this);
      this.updateVisibility = this.updateVisibility.bind(this);

      this._bindEvents();
      this._sync();
      this.updateVisibility();
    }

    activeMode() {
      return this.mql.matches ? this.modeMobile : this.modeDesktop;
    }

    // Linha de disparo do modo 'button': altura do header + folga.
    triggerLine() {
      const header = document.querySelector('.site-header');
      const h = header ? header.offsetHeight : 0;
      return h + 20;
    }

    updateVisibility() {
      const mode = this.activeMode();
      let show = false;

      if (mode === 'always') {
        show = true;
      } else if (mode === 'scroll') {
        show = window.pageYOffset > 50;
      } else if (mode === 'button') {
        show = this.realBtn ? this.realBtn.getBoundingClientRect().bottom < this.triggerLine() : false;
      }

      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this._compensate(show);
    }

    // Empurra um elemento fixo pra cima (base + altura da barra) quando visível; restaura ao sumir.
    _shiftFloating(selector, key, show) {
      let entry = this._floatCache[key];
      if (!entry) {
        const el = document.querySelector(selector);
        if (!el) return; // ainda não renderizado (ex.: app do WhatsApp carregando)
        entry = { el: el, base: parseFloat(getComputedStyle(el).bottom) || 0 };
        this._floatCache[key] = entry;
      }
      entry.el.style.bottom = show ? entry.base + this.offsetHeight + 'px' : '';
    }

    _compensate(show) {
      this._shiftFloating('back-to-top', 'backToTop', show);
      this._shiftFloating('#wa-preview-corner-button-container, .wa-preview-corner-button-container', 'whatsapp', show);
    }

    _mirrorPrice() {
      const price = document.getElementById('ProductPrice');
      const compare = document.getElementById('ComparePrice');

      if (price && this.priceEl) this.priceEl.innerHTML = price.innerHTML;

      if (this.compareEl) {
        if (compare) {
          this.compareEl.innerHTML = compare.innerHTML;
          this.compareEl.style.display = '';
        } else {
          this.compareEl.style.display = 'none';
        }
      }
    }

    _mirrorCta() {
      if (!this.realBtn || !this.cta) return;

      const disabled = this.realBtn.disabled || this.realBtn.hasAttribute('disabled');
      this.cta.disabled = disabled;

      const realText = this.realBtn.querySelector('[data-add-to-cart-text]');
      if (this.customLabel && !disabled) {
        this.cta.textContent = this.customLabel;
      } else if (realText) {
        this.cta.textContent = realText.textContent.trim();
      }
    }

    _mirrorVariant() {
      if (!this.variantEl || !this.productInfo) return;

      const vals = [];
      this.productInfo
        .querySelectorAll('input[type="radio"][name^="option-"]:checked')
        .forEach(function (r) {
          if (r.value) vals.push(r.value);
        });

      if (!vals.length) {
        this.productInfo
          .querySelectorAll('select[name^="option-"]')
          .forEach(function (s) {
            if (s.value) vals.push(s.value);
          });
      }

      this.variantEl.textContent = vals.length ? '(' + vals.join(' / ') + ')' : '';
    }

    _sync() {
      this._mirrorPrice();
      this._mirrorCta();
      this._mirrorVariant();
    }

    _onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(
        function () {
          this._raf = null;
          this.updateVisibility();
        }.bind(this)
      );
    }

    _bindEvents() {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });

      // Troca de breakpoint (desktop <-> mobile) re-avalia o modo ativo.
      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this.updateVisibility);
      } else if (this.mql.addListener) {
        this.mql.addListener(this.updateVisibility);
      }

      // <product-information> re-renderiza os nós marcados na troca de variação → re-espelha.
      if (this.productInfo) {
        let rafSync;
        this._mo = new MutationObserver(
          function () {
            if (rafSync) return;
            rafSync = requestAnimationFrame(
              function () {
                rafSync = null;
                this._sync();
              }.bind(this)
            );
          }.bind(this)
        );
        this._mo.observe(this.productInfo, { childList: true, subtree: true, characterData: true });
      }

      // CTA — comportamento Champion.
      if (this.cta) {
        this.cta.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            if (this.cta.disabled) return;

            if (this.hasVariants) {
              const options = document.querySelector('[data-product-options]');
              if (options) {
                options.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
              }
            }

            if (this.realBtn) this.realBtn.click();
          }.bind(this)
        );
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      if (this._mo) this._mo.disconnect();
    }
  }

  if (!customElements.get('sticky-buy')) {
    customElements.define('sticky-buy', StickyBuy);
  }
})();
